import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { ImagePlus, UploadCloud, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Container, Grid, Box } from '@mui/material';
import adminServices from '../../../services/adminServices';
import Page from '../../../components/Page';
import LoginForm from '../../login/components/LoginForm';
import ImagePanel from '../../login/components/ImagePanel';
import Logo from '../../../assets/logo/logo.png';

const LOGIN_IMAGE_KEY = 'websiteImages/Loginimage.jpg';

// The preview renders the login page on a screen of this size, then scales it down to fit the page.
const SCREEN_WIDTH = 1280;
const SCREEN_HEIGHT = 760;

// Same structure and ids as login/Index.jsx, so the app's global styles for `#loginFormIs` and
// `#hideSlider` (index.css) apply here exactly as they do on the login page.
const SCREEN_STYLE = {
    width: SCREEN_WIDTH,
    height: SCREEN_HEIGHT,
    transformOrigin: 'top left',
    pointerEvents: 'none',
    background: '#F0F0F0',
    display: 'flex',
    alignItems: 'center',
};
const FORM_CARD_SX = {
    background: '#FFFFFF',
    padding: '36px',
    borderRadius: '20px',
    width: '100%',
    height: '660px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
};

// login/Index.jsx registers these globally for `#hideSlider`; they are not loaded on this page.
const PREVIEW_CSS = `
#hideSlider .carousel,
#hideSlider .carousel .slider-wrapper,
#hideSlider .carousel .slider,
#hideSlider .carousel .slide,
#hideSlider .carousel .slide > div { height: 100%; }
#hideSlider .carousel .slide > div { display: flex; align-items: center; justify-content: center; }
#hideSlider .carousel .slide img { max-width: 100%; max-height: 100%; object-fit: contain; margin: auto; }`;

const noop = () => {};

const ALLOWED_TYPES = { 'image/jpeg': 'JPG', 'image/png': 'PNG', 'image/webp': 'WEBP' };
const ACCEPT = Object.keys(ALLOWED_TYPES).join(',');
const TYPES_TEXT = Object.values(ALLOWED_TYPES).join(', ');
// The login page image must be exactly this size (px).
const REQUIRED_WIDTH = 1494;
const REQUIRED_HEIGHT = 996;
const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
const SNACKBAR_MS = 4000;
const SNACKBAR_ALERT_SX = {
    backgroundColor: '#4B5563',
    color: '#fff',
    '& .MuiAlert-icon': { color: '#fff' },
};

const MESSAGES = {
    unsupportedType: `Unsupported file format. Please upload a ${TYPES_TEXT} image.`,
    tooLarge: (size) => `Image is ${size}. Please upload an image within the ${MAX_SIZE_MB} MB size limit.`,
    unreadable: 'This file could not be read as an image. Please choose a different file.',
    wrongResolution: (width, height) => `Image resolution must be exactly ${REQUIRED_WIDTH} × ${REQUIRED_HEIGHT}px. The selected image is ${width} × ${height}px.`,
    success: 'Login page image updated successfully',
    uploadFailed: 'Unable to upload the image. Please try again.',
    forbidden: 'You do not have permission to update the login page image.',
    network: 'Network error. Please check your connection and try again.',
    generic: 'Something went wrong',
};

const BTN = 'rounded-xl px-4 py-2.5 text-sm transition-all disabled:cursor-not-allowed disabled:opacity-50';
const BTN_PRIMARY = `${BTN} flex items-center gap-2 bg-[rgb(37,99,235)] font-bold text-white shadow-lg shadow-blue-200 hover:bg-[rgb(29,78,216)] disabled:shadow-none`;
const BTN_SECONDARY = `${BTN} border border-gray-300 bg-white font-semibold text-gray-700 hover:bg-gray-50`;
const MSG_ERROR = 'mt-3 flex items-start gap-2 text-sm text-red-600';

function fileToBase64React(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

const readImageDimensions = (url) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = url;
});

const formatSize = (bytes) => (bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(2)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`);

const validateImageFile = (file) => {
    if (!ALLOWED_TYPES[file.type]) return MESSAGES.unsupportedType;
    if (file.size > MAX_SIZE_BYTES) return MESSAGES.tooLarge(formatSize(file.size));
    return null;
};

const getUploadError = (response) => {
    if (response.status === 403) return MESSAGES.forbidden;
    if (response.problem === 'NETWORK_ERROR' || response.problem === 'TIMEOUT_ERROR') return MESSAGES.network;
    return response.data?.message || MESSAGES.uploadFailed;
};

// Revokes a blob URL when it changes or the component unmounts.
const useRevokeOnChange = (url) => {
    useEffect(() => () => {
        if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
    }, [url]);
};

// The real login screen (sign-in card + image carousel) drawn at full size and scaled to the
// available width. It is display-only: inert, so nothing in it can be focused or submitted.
// Without `imageSrc` the carousel shows the image the login page currently serves.
const LoginPagePreview = ({ imageSrc }) => {
    const frameRef = useRef(null);
    const screenRef = useRef(null);
    const [scale, setScale] = useState(1);

    // Layout effect: measures before the first paint, so the preview never flashes at full size.
    useLayoutEffect(() => {
        const frame = frameRef.current;
        const update = () => setScale(Math.min(1, frame.clientWidth / SCREEN_WIDTH));
        update();
        // The scale changes the frame's own height, so resizing is deferred to the next frame
        // to avoid the "ResizeObserver loop" error.
        let raf;
        const observer = new ResizeObserver(() => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(update);
        });
        observer.observe(frame);
        return () => {
            cancelAnimationFrame(raf);
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        screenRef.current.inert = true;
    }, []);

    return (
        <div
            ref={frameRef}
            className="w-full overflow-hidden rounded-xl border border-gray-200"
            style={{ height: SCREEN_HEIGHT * scale }}
        >
            <style>{PREVIEW_CSS}</style>
            <div ref={screenRef} aria-hidden="true" style={{ ...SCREEN_STYLE, transform: `scale(${scale})` }}>
                <Container maxWidth="lg" sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', px: 3 }}>
                    <Grid container spacing={2} alignItems="flex-end" sx={{ justifyContent: 'center !important' }}>
                        <Grid item xs={4.5} sx={{ margin: 'auto' }}>
                            <Box id="loginFormIs" sx={FORM_CARD_SX}>
                                <LoginForm
                                    savedEmail=""
                                    savedPassword=""
                                    loading={false}
                                    disableEmail={false}
                                    statusAlert={null}
                                    emailError=""
                                    onEmailFieldChange={noop}
                                    logoSrc={Logo}
                                    onSubmit={noop}
                                    onForgotPassword={noop}
                                    onSignUp={noop}
                                    onDismissAlert={noop}
                                    onGoogleSuccess={noop}
                                    onGoogleError={noop}
                                />
                            </Box>
                        </Grid>
                        <Grid id="hideSlider" item xs={7.5}>
                            <ImagePanel screen="login" firstImage={imageSrc} />
                        </Grid>
                    </Grid>
                </Container>
            </div>
        </div>
    );
};

LoginPagePreview.propTypes = {
    imageSrc: PropTypes.string,
};

LoginPagePreview.defaultProps = {
    imageSrc: null,
};

// The selected image at the proportions it is displayed in; `busy` overlays a spinner while uploading.
const ImagePreview = ({ src, busy, className }) => (
    <div
        className={`relative overflow-hidden rounded-xl bg-gray-100 ${className}`}
        style={{ aspectRatio: `${REQUIRED_WIDTH} / ${REQUIRED_HEIGHT}` }}
    >
        <img src={src} alt="Selected login page" className="h-full w-full object-cover" />
        {busy && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white">
                <Loader2 size={24} className="animate-spin" />
            </div>
        )}
    </div>
);

ImagePreview.propTypes = {
    src: PropTypes.string.isRequired,
    busy: PropTypes.bool,
    className: PropTypes.string,
};

ImagePreview.defaultProps = {
    busy: false,
    className: '',
};

export default function WebsiteImageUploader({ imageKey }) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadedUrl, setUploadedUrl] = useState(null);
    // Local copy of the image just uploaded; the CDN can keep serving the old one for a few minutes.
    const [uploadedImage, setUploadedImage] = useState(null);
    const [error, setError] = useState(null);
    const [fileError, setFileError] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const inputRef = useRef(null);
    // Counts file selections, so a result that arrives after a newer pick can be ignored.
    const selectionRef = useRef(0);

    useRevokeOnChange(preview);
    useRevokeOnChange(uploadedImage);

    const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
    const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

    const resetSelection = () => {
        selectionRef.current += 1;
        setFile(null);
        setPreview(null);
    };

    const clearSelection = () => {
        resetSelection();
        setFileError(null);
        setError(null);
    };

    const selectFile = async (selected) => {
        if (!selected) return;
        setError(null);
        setUploadedUrl(null);

        const validationError = validateImageFile(selected);
        if (validationError) {
            setFileError(validationError);
            return;
        }

        const url = URL.createObjectURL(selected);
        selectionRef.current += 1;
        const selection = selectionRef.current;

        try {
            const { width, height } = await readImageDimensions(url);
            if (selection !== selectionRef.current) {
                URL.revokeObjectURL(url);
                return;
            }
            if (width !== REQUIRED_WIDTH || height !== REQUIRED_HEIGHT) {
                URL.revokeObjectURL(url);
                setFileError(MESSAGES.wrongResolution(width, height));
                return;
            }
            setFileError(null);
            setFile(selected);
            setPreview(url);
        } catch {
            URL.revokeObjectURL(url);
            if (selection === selectionRef.current) setFileError(MESSAGES.unreadable);
        }
    };

    const handleFileChange = (e) => {
        selectFile(e.target.files[0]);
        // allow re-selecting the same file after removing it
        e.target.value = '';
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        if (!uploading) selectFile(e.dataTransfer.files?.[0]);
    };

    const handleUpload = async () => {
        // `uploading` also blocks a second click while the dialog is still closing
        if (!file || uploading) return;
        setConfirmOpen(false);
        setUploading(true);
        setError(null);

        try {
            const base64Image = await fileToBase64React(file);

            const body = {
                key: imageKey,
                mimeType: file.type,
                image: base64Image
            };

            const response = await adminServices.LoginPageImage(body);

            if (response.ok) {
                setUploadedUrl(response.data.url);
                setUploadedImage(URL.createObjectURL(file));
                resetSelection();
                showSnackbar(MESSAGES.success);
            } else {
                const message = getUploadError(response);
                setError(message);
                showSnackbar(message, 'error');
            }
        } catch (err) {
            setError(err.message);
            showSnackbar(MESSAGES.generic, 'error');
        } finally {
            setUploading(false);
        }
    };

    // const handleUpload = async () => {
    //     if (!file) return;
    //     setUploading(true);
    //     setError(null);

    //     try {
    //         const base64Image = await fileToBase64React(file);
    //         const body = JSON.stringify({
    //             key: imageKey,
    //             mimeType: file.type,
    //             image: base64Image
    //         })
    //         const response = await adminServices.LoginPageImage(body)

    //         // const response = await fetch('/api/websiteImageUpload', {
    //         //     method: 'PUT',
    //         //     headers: {
    //         //         'Content-Type': 'application/json',
    //         //     },
    //         //     body: JSON.stringify({
    //         //         key: imageKey,
    //         //         mimeType: file.type,
    //         //         image: base64Image
    //         //     })
    //         // });

    //         // if (!response.ok) {
    //         //     const errBody = await response.json().catch(() => ({}));
    //         //     throw new Error(errBody.message || `Upload failed (${response.status})`);
    //         // }

    //         setUploadedUrl(response.data.url);
    //     } catch (err) {
    //         setError(err.message);
    //     } finally {
    //         setUploading(false);
    //     }
    // };

    const openPicker = () => !uploading && inputRef.current?.click();

    return (
        <Page title="Login Page Config">
            <div className="mt-4 p-6">
                <div className="mb-6">
                    <div className="flex items-center gap-2.5 mb-1">
                        <ImagePlus size={22} className="text-blue-600" />
                        <h2 className="text-xl font-extrabold tracking-tight text-gray-900">Login Page Image</h2>
                    </div>
                    <p className="text-sm text-gray-500">
                        Replace the banner image shown beside the sign-in form. The change goes live for all users as soon as it is uploaded.
                    </p>
                </div>

                <section className="w-full max-w-6xl rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="mb-4 flex items-center justify-between gap-3">
                        <h3 className="text-sm font-bold text-gray-900">Login page preview</h3>
                        {file ? (
                            <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-100 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-600">
                                New image &bull; not saved yet
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-md border border-green-100 bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-600">
                                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                                Live
                            </span>
                        )}
                    </div>

                    <LoginPagePreview imageSrc={preview || uploadedImage} />
                    <p className="mt-2 text-xs text-gray-500">
                        Shows how visitors see the login page. Your image is the first slide; the other slides rotate after it.
                    </p>

                    {file ? (
                        <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-3">
                            <ImagePreview src={preview} busy={uploading} className="w-full" />
                            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                                <span className="max-w-[60%] truncate font-semibold text-gray-800" title={file.name}>{file.name}</span>
                                <span className="text-gray-500">
                                    {formatSize(file.size)}
                                    {` • ${REQUIRED_WIDTH} × ${REQUIRED_HEIGHT}px`}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={openPicker}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openPicker();
                                }
                            }}
                            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                            onDragLeave={() => setDragActive(false)}
                            onDrop={handleDrop}
                            className={`mt-4 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed px-6 py-6 text-center transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/50'
                            }`}
                        >
                            <UploadCloud size={22} className="text-blue-600" />
                            <p className="text-sm font-semibold text-gray-800">
                                Drag and drop a new image here, or <span className="text-blue-600">browse</span>
                            </p>
                            <p className="text-xs text-gray-500">
                                {TYPES_TEXT} &bull; Max {MAX_SIZE_MB} MB &bull; Exactly {REQUIRED_WIDTH} &times; {REQUIRED_HEIGHT}px
                            </p>
                        </div>
                    )}

                    <input ref={inputRef} type="file" accept={ACCEPT} onChange={handleFileChange} className="hidden" />

                    {fileError && (
                        <p role="alert" className={MSG_ERROR}>
                            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                            {fileError}
                        </p>
                    )}
                    {error && (
                        <p role="alert" className={MSG_ERROR}>
                            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                            {error}
                        </p>
                    )}
                    {uploadedUrl && !file && (
                        <p className="mt-3 flex items-start gap-2 text-sm text-green-600">
                            <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                            <span>
                                Image uploaded. It can take a few minutes to appear for visitors.{' '}
                                <a href={uploadedUrl} target="_blank" rel="noreferrer" className="font-semibold underline">View image</a>
                            </span>
                        </p>
                    )}

                    {file && (
                        <div className="mt-5 flex justify-end gap-3">
                            <button type="button" onClick={clearSelection} disabled={uploading} className={BTN_SECONDARY}>
                                Cancel
                            </button>
                            <button type="button" onClick={() => setConfirmOpen(true)} disabled={uploading} className={BTN_PRIMARY}>
                                {uploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
                                {uploading ? 'Uploading...' : 'Replace Image'}
                            </button>
                        </div>
                    )}
                </section>
            </div>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                        <AlertTriangle size={20} />
                    </span>
                    Replace login page image?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The new image will replace the current one on the login page for all users. 
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <button type="button" onClick={() => setConfirmOpen(false)} className={BTN_SECONDARY}>Cancel</button>
                    <button type="button" onClick={handleUpload} className={BTN_PRIMARY}>Yes, replace</button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={SNACKBAR_MS}
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled" sx={SNACKBAR_ALERT_SX}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Page>
    );
}

WebsiteImageUploader.propTypes = {
    imageKey: PropTypes.string,
};

WebsiteImageUploader.defaultProps = {
    imageKey: LOGIN_IMAGE_KEY,
};
